import { createContext, lazy, Suspense, useCallback, useContext, useMemo, useRef, useState } from 'react';

const IssueReportModal = lazy(() => import('../components/IssueReport/IssueReportModal'));

const IssueReportContext = createContext(null);

/**
 * Lets any view open the "Report a problem" dialog, and lets App publish what
 * the user is looking at (view, session, laps…) so the report carries it
 * without the dialog having to know the app's state shape. The dialog mounts
 * here — once, above AppInner's early returns — for the same reason
 * ShortcutOverlay does.
 */
export function IssueReportProvider({ currentUser, enabled = true, children }) {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const pageContextRef = useRef({});

  const openIssueReport = useCallback((initial = null) => {
    setPrefill(initial);
    setOpen(true);
  }, []);
  const closeIssueReport = useCallback(() => setOpen(false), []);
  // A ref, not state: App updates this on every relevant render and nothing
  // needs to re-render because of it — the dialog reads it when it opens.
  const setPageContext = useCallback((ctx) => { pageContextRef.current = ctx || {}; }, []);

  const value = useMemo(() => ({
    available: enabled,
    openIssueReport,
    closeIssueReport,
    setPageContext,
  }), [enabled, openIssueReport, closeIssueReport, setPageContext]);

  return (
    <IssueReportContext.Provider value={value}>
      {children}
      {enabled && open && (
        <Suspense fallback={null}>
          <IssueReportModal
            onClose={closeIssueReport}
            prefill={prefill}
            pageContext={pageContextRef.current}
            currentUser={currentUser}
          />
        </Suspense>
      )}
    </IssueReportContext.Provider>
  );
}

const UNAVAILABLE = {
  available: false,
  openIssueReport: () => {},
  closeIssueReport: () => {},
  setPageContext: () => {},
};

export function useIssueReport() {
  return useContext(IssueReportContext) || UNAVAILABLE;
}
